import { Fragment } from "react";
import { markdown, ColorSpecimen, ColorPaletteSpecimen } from "catalog";
import * as colors from "./colors";
import * as sszvis from "sszvis";
import { range, rgb } from "d3";
var convert = require("color-convert");
const specimenColors = (o) => {
    return Object.keys(o).map(k => ({ name: k, value: o[k] }));
};
const ordinalScaleColors = (s) => s.range().map((color, index) => ({
    name: index,
    value: colorToHex(color)
}));
const sequentialScaleColors = (scale, number) => {
    const step = 1 / (number - 1);
    scale.domain([0, 1]);
    return range(number).map(i => ({
        name: i,
        value: colorToHex(rgb(scale(i * step)))
    }));
};
const colorToHex = (c) => {
    // Helper to format a number in two-character hex representation
    // eg. 0 -> "00", 15 -> "0f", 255 -> "ff"
    const hex = (n) => Math.round(n)
        .toString(16)
        .padStart(2, "0");
    const { r, g, b } = rgb(c.toString()).rgb();
    return `#${hex(r)}${hex(g)}${hex(b)}`;
};
export default () => markdown `

## Primary Colors
${(<Fragment>
    <ColorSpecimen value={colors.primaryColors.zuerichBlue} span={3} name={"zuerichBlue"}/>
    <ColorSpecimen value={colors.primaryColors.white} span={2} name={"white"}/>
    <ColorSpecimen value={colors.primaryColors.darkGrey} span={1} name={"darkGrey"}/>
  </Fragment>)}

## Secondary Colors
${(<Fragment>
    {Object.entries(colors.secondaryColors).map(([k, value]) => {
    return <ColorSpecimen key={k} name={k} span={2} value={value}/>;
})}
  </Fragment>)}

## Color Palettes
${(<ColorPaletteSpecimen span={2} colors={specimenColors(colors.zuerichBlueColors)}/>)}
${<ColorPaletteSpecimen span={2} colors={specimenColors(colors.redColors)}/>}
${<ColorPaletteSpecimen span={2} colors={specimenColors(colors.greyColors)}/>}

## Sequential Color Scales

Sequential sales are used to compare values. These scales are designed to have the same brightness for the same input value.

### sszvis.scaleSeqBlu()
${(<ColorPaletteSpecimen horizontal colors={sequentialScaleColors(sszvis.scaleSeqBlu(), 9)}/>)}
Sequential scales can be reversed using the \`scale.reverse()\` instance method. This method returns a new scale instance.
${(<ColorPaletteSpecimen horizontal colors={sequentialScaleColors(sszvis.scaleSeqBlu().reverse(), 9)}/>)}
### sszvis.scaleSeqRed()
${(<ColorPaletteSpecimen horizontal colors={sequentialScaleColors(sszvis.scaleSeqRed(), 9)}/>)}
### sszvis.scaleSeqGrn()
${(<ColorPaletteSpecimen horizontal colors={sequentialScaleColors(sszvis.scaleSeqGrn(), 9)}/>)}
### sszvis.scaleSeqBrn()
${(<ColorPaletteSpecimen horizontal colors={sequentialScaleColors(sszvis.scaleSeqBrn(), 9)}/>)}

## Qualitative Color Scales
Qualitative scales are used for data that has distinct categories. They distinguish categories based on color contrast. They should never be used to compare values, use sequential or divergent scales in this case.
#### sszvis.scaleQual12()
${(<ColorPaletteSpecimen horizontal colors={ordinalScaleColors(sszvis.scaleQual12())}/>)}
Qualitative scales can be brightened or darkened with the \`scale.brighter()\` and \`scale.darker()\` instance methods. These methods return new scale instances.
${(<ColorPaletteSpecimen horizontal colors={ordinalScaleColors(sszvis.scaleQual12().brighter())}/>)}
${(<ColorPaletteSpecimen horizontal colors={ordinalScaleColors(sszvis.scaleQual12().darker())}/>)}
Qualitative scales can be reversed with the \`scale.reverse()\` instance method. This method returns a new scale.
${(<ColorPaletteSpecimen horizontal colors={ordinalScaleColors(sszvis.scaleQual12().reverse())}/>)}
### szvis.scaleQual6()
${(<ColorPaletteSpecimen horizontal colors={ordinalScaleColors(sszvis.scaleQual6())}/>)}
### szvis.scaleQual6a()
${(<ColorPaletteSpecimen horizontal colors={ordinalScaleColors(sszvis.scaleQual6a())}/>)}
### szvis.scaleQual6b()
${(<ColorPaletteSpecimen horizontal colors={ordinalScaleColors(sszvis.scaleQual6b())}/>)}

## Divergent Color Scales
Divergent sales are used to compare data that has two extremes. These scales are designed to have the same brightness for the same input value. They come in two color variations: the valued (red-blue) variation is used for data that has negative-positive characteristics, the neutral (brown-green) variation is used in cases where no valuation is wanted.
### sszvis.scaleDivVal()
${(<ColorPaletteSpecimen horizontal colors={sequentialScaleColors(sszvis.scaleDivVal(), 9)}/>)}
Divergent scales can be reversed using the \`scale.reverse()\` instance method. This method returns a new scale instance.
${(<ColorPaletteSpecimen horizontal colors={sequentialScaleColors(sszvis.scaleDivVal().reverse(), 9)}/>)}
### sszvis.scaleDivNtr()
${(<ColorPaletteSpecimen horizontal colors={sequentialScaleColors(sszvis.scaleDivNtr(), 9)}/>)}
### sszvis.scaleDivValGry()
A grey midpoint can be used in situations where the contour of a data mark must be visible on a white background.
${(<ColorPaletteSpecimen horizontal colors={sequentialScaleColors(sszvis.scaleDivValGry(), 9)}/>)}
### sszvis.scaleDivNtrGry()
${(<ColorPaletteSpecimen horizontal colors={sequentialScaleColors(sszvis.scaleDivNtrGry(), 9)}/>)}

`;



// WEBPACK FOOTER //
// ./src/theme/colors.docs.tsx